import { ClientOptions } from "./types/client";
import {
  ParamsByLocation,
  PathsWithMethod,
  ResponseFor,
  SafeParamsFor,
  UntypedParams,
} from "./types/internal";
import { PathsShapeT } from "./types/openapi";
import { HttpMethod } from "./types/util";
import {
  allAsString,
  buildMultipartFormData,
  buildQueryParams,
  isSupportedReturnType,
  replaceParams,
} from "./util";

export default class SafeHttpClient<
  Paths extends PathsShapeT,
  Prefix extends string,
  ProducesType extends string = "application/json",
  ConsumesType extends string = "application/json",
> {
  #name: string;
  #prefix: Prefix;
  #produces: ProducesType;
  #consumes: ConsumesType;
  #options: Required<ClientOptions>;
  #baseUrl: string;

  constructor(
    name: string,
    baseUrl: string,
    prefix: Prefix,
    produces: ProducesType,
    consumes: ConsumesType,
    clientOptions?: ClientOptions,
  ) {
    if (/\/+$/.test(prefix)) {
      throw new Error("The prefix is not allowed to have trailing slashes");
    }
    if (/\/+$/.test(baseUrl)) {
      throw new Error("The baseUrl is not allowed to have trailing slashes");
    }

    this.#prefix = prefix;
    this.#baseUrl = baseUrl;
    this.#produces = produces;
    this.#consumes = consumes;
    this.#name = name;
    this.#options = {
      fetchFn: clientOptions?.fetchFn ?? fetch.bind(globalThis),
      onContentTypeError: clientOptions?.onContentTypeError ?? (() => {}),
      queryArrayParamStyle: clientOptions?.queryArrayParamStyle ?? "comma",
    };
  }

  withProduces<NewProducesType extends string>(
    type: NewProducesType,
  ): SafeHttpClient<Paths, Prefix, NewProducesType, ConsumesType> {
    return new SafeHttpClient(
      this.#name,
      this.#baseUrl,
      this.#prefix,
      type,
      this.#consumes,
      this.#options,
    );
  }
  withConsumes<NewConsumesType extends string>(
    type: NewConsumesType,
  ): SafeHttpClient<Paths, Prefix, ProducesType, NewConsumesType> {
    return new SafeHttpClient(
      this.#name,
      this.#baseUrl,
      this.#prefix,
      this.#produces,
      type,
      this.#options,
    );
  }
  withPrefix<NewPrefix extends string>(
    prefix: NewPrefix,
  ): SafeHttpClient<Paths, NewPrefix, ProducesType, ConsumesType> {
    return new SafeHttpClient(
      this.#name,
      this.#baseUrl,
      prefix,
      this.#produces,
      this.#consumes,
      this.#options,
    );
  }

  private async _request(
    method: Uppercase<HttpMethod>,
    path: PathsWithMethod<Paths, HttpMethod, Prefix>,
    params?: ParamsByLocation,
    request?: RequestInit,
  ): Promise<{ status: number; data: unknown; raw: Response }> {
    const query = params?.query ?? {};
    let qp =
      this.#options.queryArrayParamStyle === "comma"
        ? new URLSearchParams(allAsString(query)).toString()
        : buildQueryParams(query, this.#options.queryArrayParamStyle).toString();
    if (qp) {
      qp = "?" + qp;
    }
    const url = this.#baseUrl + this.#prefix + path;
    const body =
      params === undefined || params === null || params.body === undefined
        ? undefined
        : this.#produces === "multipart/form-data"
          ? buildMultipartFormData(params.body)
          : JSON.stringify(params.body);
    const writeHeaders =
      body === undefined || this.#produces === "multipart/form-data"
        ? null
        : {
            "Content-Type": this.#produces,
          };
    const response = await this.#options.fetchFn(
      replaceParams(url, allAsString(params?.path ?? {})) + qp,
      {
        credentials: "same-origin",
        ...(request ?? {}),
        method: method,
        headers: {
          ...(writeHeaders ?? {}),
          "Accept": this.#consumes,
          ...(request?.headers ?? {}),
          ...allAsString(params?.headers ?? {}),
          "X-OpenAI-Client": this.#name,
        },
        body: body,
      },
    );

    const returnType = response.headers.get("Content-Type");
    if (!returnType) {
      this.#options.onContentTypeError?.(response);
      throw new Error("Content-Type was not provided with the response");
    }
    if (!isSupportedReturnType(returnType)) {
      this.#options.onContentTypeError?.(response);
      throw new Error(`Unsupported return content type ${returnType}`);
    }
    if (returnType !== this.#consumes) {
      this.#options.onContentTypeError?.(response);
      throw new Error(
        `Return content type ${returnType} was provided but this client only accepts ${this.#consumes}.`,
      );
    }
    switch (returnType) {
      case "application/json":
        const result = await response.json();
        return { status: response.status, data: result, raw: response };
      case "application/vnd.apache.arrow.stream":
        const buffer = await response.arrayBuffer();
        return { status: response.status, data: buffer, raw: response };
    }
  }

  async get<Pathname extends PathsWithMethod<Paths, "get", Prefix>>(
    path: Pathname,
    ...params: SafeParamsFor<Paths, "get", `${Prefix}${Pathname}`, ProducesType>
  ): Promise<ResponseFor<Paths, "get", `${Prefix}${Pathname}`, ConsumesType>> {
    return (await this._request("GET", path, ...(params as UntypedParams))) as ResponseFor<
      Paths,
      "get",
      `${Prefix}${Pathname}`,
      ConsumesType
    >;
  }

  async post<Pathname extends PathsWithMethod<Paths, "post", Prefix>>(
    path: Pathname,
    ...params: SafeParamsFor<Paths, "post", `${Prefix}${Pathname}`, ProducesType>
  ): Promise<ResponseFor<Paths, "post", `${Prefix}${Pathname}`, ConsumesType>> {
    return (await this._request("POST", path, ...(params as UntypedParams))) as ResponseFor<
      Paths,
      "post",
      `${Prefix}${Pathname}`,
      ConsumesType
    >;
  }

  async put<Pathname extends PathsWithMethod<Paths, "put", Prefix>>(
    path: Pathname,
    ...params: SafeParamsFor<Paths, "put", `${Prefix}${Pathname}`, ProducesType>
  ): Promise<ResponseFor<Paths, "put", `${Prefix}${Pathname}`, ConsumesType>> {
    return (await this._request("PUT", path, ...(params as UntypedParams))) as ResponseFor<
      Paths,
      "put",
      `${Prefix}${Pathname}`,
      ConsumesType
    >;
  }

  async patch<Pathname extends PathsWithMethod<Paths, "patch", Prefix>>(
    path: Pathname,
    ...params: SafeParamsFor<Paths, "patch", `${Prefix}${Pathname}`, ProducesType>
  ): Promise<ResponseFor<Paths, "patch", `${Prefix}${Pathname}`, ConsumesType>> {
    return (await this._request("PATCH", path, ...(params as UntypedParams))) as ResponseFor<
      Paths,
      "patch",
      `${Prefix}${Pathname}`,
      ConsumesType
    >;
  }

  async delete<Pathname extends PathsWithMethod<Paths, "delete", Prefix>>(
    path: Pathname,
    ...params: SafeParamsFor<Paths, "delete", `${Prefix}${Pathname}`, ProducesType>
  ): Promise<ResponseFor<Paths, "delete", `${Prefix}${Pathname}`, ConsumesType>> {
    return (await this._request("DELETE", path, ...(params as UntypedParams))) as ResponseFor<
      Paths,
      "delete",
      `${Prefix}${Pathname}`,
      ConsumesType
    >;
  }

  async head<Pathname extends PathsWithMethod<Paths, "head", Prefix>>(
    path: Pathname,
    ...params: SafeParamsFor<Paths, "head", `${Prefix}${Pathname}`, ProducesType>
  ): Promise<ResponseFor<Paths, "head", `${Prefix}${Pathname}`, ConsumesType>> {
    return (await this._request("HEAD", path, ...(params as UntypedParams))) as ResponseFor<
      Paths,
      "head",
      `${Prefix}${Pathname}`,
      ConsumesType
    >;
  }
}
